import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {
    safelyGetFilterByKey,
    safelyGetRelationshipFilterIdsFromFilterValue,
} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {ExpandedFilterBar} from 'shells/control-bar/expanded-filter-bar';
import {
    getArchivedValue,
    getCampaignStatusesForQuery,
    getLastUpdatedValue,
} from 'shells/control-bar/helpers';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
import {PaginatedView} from 'shells/list-view/paginated-view';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import type {TemplateStubFragment as Template, SessionUser} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';
import {writeLocalStorage} from '../../../util/localStorage';

import {GetSessionUser} from '../../core/get-session-user';
import {PAGE_CONTAINER_CONTENT_ID, PageContainer, PageTitleBar} from '../../layout';

import {checkIsFiltering} from '../email-marketing-helpers';
import {MarketingStatsBanner} from '../marketing-stats-banner/marketing-stats-banner';

import {NewTemplateModal} from './new-template-modal';
import {TemplateListQuery, getTemplateListQueryVariables} from './template-list-query';
import {TemplateMutations} from './template-mutations';
import {TemplatesList} from './templates-list';

type OwnProps = ContextRouter & {
    urlParams: any;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    onUpdateFilters: (arg1: FilterObject) => void;
    onRemoveAllFilters: () => void;
};

type Props = OwnProps & {
    templates: Template[];
    onDuplicateTemplate: (id: string, name?: string | null | undefined) => Promise<any>;
    onDeleteTemplate: (arg1: string) => Promise<any>;
    onRestoreTemplate: (arg1: string) => Promise<any>;
    isLoading: boolean;
    isFirstLoad: boolean;
    error: string | null | undefined;
    searchValue: string;
    onParamChange: (arg1: any, arg2: boolean) => any;
    activeFilters: FilterObject[];
    onUpdateFilters: (arg1: FilterObject) => void;
    hasNextPage: boolean;
    onFetchMoreRows: () => Promise<any>;
    user: SessionUser;
};

const TEMPLATE_VIEW_SEARCH = 'TEMPLATE_VIEW_SEARCH';

function TemplatesListPageComponent(props: Props) {
    const [isAddingNewTemplate, setIsAddingNewTemplate] = React.useState<boolean>(false);
    const [duplicatingTemplate, setDuplicatingTemplate] = React.useState<
        Template | null | undefined
    >(null);
    const [isDuplicatingTemplate, setIsDuplicatingTemplate] = React.useState<boolean>(false);

    React.useEffect(() => {
        writeLocalStorage(TEMPLATE_VIEW_SEARCH, props.location.search);
    }, [props.location.search]);

    const isShowingArchived = safelyGetFilterByKey(props.activeFilters, 'archived') === 'true';

    const isFiltering = checkIsFiltering(props.activeFilters, props.searchValue);

    return (
        <>
            <PageContainer
                title='Templates'
                pageTitleBar={
                    <PageTitleBar
                        title='Templates'
                        subheaderPrimaryText='Draft email marketing templates for your campaigns'
                        buttons={
                            <Button
                                variant='primary'
                                size='big'
                                onClick={() => {
                                    setIsAddingNewTemplate(true);
                                }}
                            >
                                Create new template
                            </Button>
                        }
                    />
                }
                controlBar={
                    <ListPageControlBar
                        archiveTab={{
                            isActive: isShowingArchived,
                            onChange: () => {
                                props.onUpdateFilters({
                                    archived: isShowingArchived ? null : true,
                                });
                            },
                        }}
                        leftSection={
                            <ExpandedFilterBar
                                searchFilterProps={{
                                    searchValue: props.searchValue,
                                    onUpdateSearch: (newSearchValue) => {
                                        props.onParamChange({q: newSearchValue}, true);
                                    },
                                }}
                                isFiltering={isFiltering}
                                noun='template'
                                count={props.templates.length}
                                onUpdateFilters={props.onUpdateFilters}
                                onRemoveAllFilters={props.onRemoveAllFilters}
                                // @ts-expect-error
                                activeFilters={props.activeFilters}
                            />
                        }
                    />
                }
            >
                <PaginatedView
                    scrollContainerId={PAGE_CONTAINER_CONTENT_ID}
                    hasNextPage={props.hasNextPage}
                    isLoading={props.isLoading && !props.isFirstLoad && !isFiltering}
                    onFetchMoreRows={props.onFetchMoreRows}
                    renderBody={
                        <TemplatesList
                            templates={props.templates}
                            onDeleteTemplate={props.onDeleteTemplate}
                            onRestoreTemplate={props.onRestoreTemplate}
                            isLoading={props.isLoading}
                            isFiltering={isFiltering}
                            error={props.error}
                            setDuplicatingTemplate={setDuplicatingTemplate}
                            setIsAddingNewTemplate={setIsAddingNewTemplate}
                            hasFullAccessToCampaigns={props.user.permissions.canAccessFullCampaigns}
                        />
                    }
                />
                <MarketingStatsBanner isImportUnsubscribesBannerNeeded={true} />
            </PageContainer>
            <NewTemplateModal
                isOpen={isAddingNewTemplate}
                onClose={() => {
                    setIsAddingNewTemplate(false);
                }}
            />
            {duplicatingTemplate ? (
                <SingleInputModal
                    requireValue={true}
                    buttonText='Create'
                    initialValue={`Copy of ${duplicatingTemplate.name}`}
                    title='Name your new template'
                    subtitle='Add a descriptive name so you can find it later'
                    placeholder={`Copy of ${duplicatingTemplate.name}`}
                    isSaving={isDuplicatingTemplate}
                    onClose={() => {
                        setDuplicatingTemplate(null);
                    }}
                    onSave={(name) => {
                        setIsDuplicatingTemplate(true);
                        const nameValue = name && name.length ? name : null;
                        props
                            .onDuplicateTemplate(duplicatingTemplate.edition.id, nameValue)
                            .then(() => {
                                setIsDuplicatingTemplate(false);
                                setDuplicatingTemplate(null);
                            });
                    }}
                />
            ) : undefined}
        </>
    );
}

export function TemplatesListPage(props: OwnProps) {
    // We've hardcoded the server's sort for now - table sorting is currently
    // handled on the client and stored in the URL
    const sortValue = 'MODIFIED_TIME_DESC';
    const searchValue = props.urlParams.q || '';
    const isArchived = getArchivedValue(props.activeFilters);

    const ownerFilter = safelyGetFilterByKey(props.activeFilters, 'owner');
    const assigneeIds = ownerFilter
        ? safelyGetRelationshipFilterIdsFromFilterValue(ownerFilter)
        : undefined;

    const queryVariables = getTemplateListQueryVariables(isArchived, sortValue, searchValue);

    const isFilteringOnCreatorUserId = Boolean(
        // @ts-expect-error
        props.activeFilters.find((filter) => {
            return Object.keys(filter)[0] === 'creatorUserId';
        })
    );

    const lastUpdatedTime = getLastUpdatedValue(props.activeFilters);
    const statuses = getCampaignStatusesForQuery(props.activeFilters);

    return (
        <GetSessionUser>
            {/* @ts-expect-error */}
            {({user}) => {
                return user ? (
                    <TemplateListQuery
                        isArchived={isArchived}
                        // @ts-expect-error
                        assigneeIds={assigneeIds}
                        sortOrder={sortValue}
                        searchQuery={searchValue}
                        creatorUserId={isFilteringOnCreatorUserId ? user.id : undefined}
                        lastUpdatedTime={lastUpdatedTime}
                        statuses={statuses && statuses.length ? statuses : undefined}
                    >
                        {({
                            templates,
                            isLoading,
                            isFirstLoad,
                            error,
                            fetchMoreRows,
                            hasNextPage,
                        }) => (
                            <TemplateMutations>
                                {({onDuplicate, onDelete, onRestore}) => (
                                    <TemplatesListPageComponent
                                        {...props}
                                        templates={templates}
                                        isLoading={isLoading}
                                        isFirstLoad={isFirstLoad}
                                        error={error}
                                        // @ts-expect-error
                                        onDuplicateTemplate={(templateId, name) =>
                                            onDuplicate(templateId, name, queryVariables).then(
                                                (newTemplate) => {
                                                    if (newTemplate)
                                                        appHistory.push(
                                                            newTemplate.edition.htmlUrlPath
                                                        );
                                                }
                                            )
                                        }
                                        // @ts-expect-error
                                        onDeleteTemplate={(templateId) => {
                                            return onDelete(templateId, queryVariables);
                                        }}
                                        // @ts-expect-error
                                        onRestoreTemplate={(templateId) => {
                                            return onRestore(templateId, queryVariables);
                                        }}
                                        searchValue={searchValue}
                                        userId={user.id}
                                        onFetchMoreRows={fetchMoreRows}
                                        hasNextPage={hasNextPage}
                                        user={user}
                                    />
                                )}
                            </TemplateMutations>
                        )}
                    </TemplateListQuery>
                ) : null;
            }}
        </GetSessionUser>
    );
}
